import React, { useState, useEffect, useRef, useContext } from 'react'
import Cookies from 'js-cookie'
import {authenticatedFetch} from '../../utils/authenticatedFetch'
import ReactMarkdown from 'react-markdown'
import { ThreeDots } from 'react-loader-spinner'
import { BsBagFill, BsX, BsStarFill, BsCheck } from 'react-icons/bs'
import { FiSend, FiShoppingCart } from 'react-icons/fi'
import { RiRobotLine } from 'react-icons/ri'
import { withRouter } from 'react-router-dom'
import CartContext from '../../context/CartContext'
import './index.css'

const INITIAL_MESSAGES = [
  {
    id: 'init-1',
    role: 'assistant',
    text: 'Hi! I am your AI Shopping Assistant.\nHow can I help you today?',
    products: [],
  },
]

const ChatAssistant = (props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputMessage, setInputMessage] = useState('')
  const [messages, setMessages] = useState(INITIAL_MESSAGES)
  const [isLoading, setIsLoading] = useState(false)
  const [addingId, setAddingId] = useState(null)

  const { addCartItem, loadCart, getCart } = useContext(CartContext)
  const messagesEndRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isLoading, isOpen])

  const handleSendMessage = async (e) => {
    e.preventDefault()
    const query = inputMessage.trim()
    if (!query || isLoading) return

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      text: query,
      products: [],
    }

    setMessages((prev) => [...prev, userMessage])
    setInputMessage('')
    setIsLoading(true)

    try {
      const response = await authenticatedFetch(`${process.env.REACT_APP_API_BASE_URL}/api/assistant/chat`, {
        method: 'POST',
        body: JSON.stringify({ message: query }),
      })

      if (!response.ok) {
        throw new Error(`Server returned HTTP ${response.status}`)
      }

      const data = await response.json()

      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        text: data.reply || "I've processed your request.",
        products: data.products || [],
      }

      setMessages((prev) => [...prev, assistantMessage])

      if (typeof loadCart === 'function') loadCart()
      else if (typeof getCart === 'function') getCart()
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          text: 'Unable to reach the assistant. Please verify your connection or login session.',
          products: [],
        },
      ])
    } finally {
      setIsLoading(false)
    }
  }

  const handleQuickAddToCart = async (product) => {
    setAddingId(product.id || product._id)
    try {
      const jwtToken = Cookies.get('jwt_token')
      if (!jwtToken) {
        console.error('No JWT token found')
        return
      }

      const payload = {
        id: product.id || product._id,
        title: product.title,
        brand: product.brand,
        price: product.price,
        imageUrl: product.imageUrl || product.image_url,
        quantity: 1,
      }

      if (typeof addCartItem === 'function') {
        await addCartItem(payload)
        props.history.push('/cart')
        setIsOpen(false)
      }
    } catch (err) {
      console.error('Failed to add item:', err)
    } finally {
      setTimeout(() => setAddingId(null), 1500)
    }
  }

  return (
    <div className="chat-assistant-container">
      {!isOpen && (
        <button
          type="button"
          className="chat-toggle-btn"
          onClick={() => setIsOpen(true)}
          aria-label="Open AI Shopping Assistant"
        >
          <div className="icon-badge-wrapper">
            <BsBagFill className="primary-shop-icon" size={25}/>
            <BsStarFill className="sparkle-badge-icon" size={14}/>
          </div>
        </button>
      )}

      {isOpen && (
        <div className="chat-window-card">
          <div className="chat-header">
            <div className="chat-header-info">
              <RiRobotLine size={20} className="header-ai-icon" />
              <h4>AI Shopping Assistant</h4>
            </div>
            <button
              type="button"
              className="chat-close-btn"
              onClick={() => setIsOpen(false)}
              aria-label="Close Assistant"
            >
              <BsX size={20}/>
            </button>
          </div>

          <div className="chat-messages-body">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`chat-bubble ${
                  msg.role === 'user' ? 'user-bubble' : 'assistant-bubble'
                }`}
              >
                <div className="markdown-content">
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>

                {msg.products && msg.products.length > 0 && (
                  <div className="chat-product-grid">
                    {msg.products.map((item) => {
                      const itemId = item.id || item._id
                      const isAdded = addingId === itemId
                      return (
                        <div key={itemId} className="chat-product-card">
                          {item.imageUrl && (
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className="chat-product-img"
                              loading="lazy"
                            />
                          )}
                          <div className="chat-product-details">
                            <p className="chat-product-title" title={item.title}>
                              {item.title}
                            </p>
                            <p className="chat-product-brand">{item.brand?.toUpperCase()}</p>
                            <div className="chat-product-meta">
                              <span className="chat-product-price">₹{item.price}</span>
                              <span className="chat-product-rating">
                                <BsStarFill color="#f59e0b" size={10}/> {item.rating}
                              </span>
                            </div>
                            <button
                              type="button"
                              className={`chat-add-btn ${isAdded ? 'added' : ''}`}
                              onClick={() => handleQuickAddToCart(item)}
                              disabled={isAdded}
                            >
                              {isAdded ? (
                                <>
                                  <BsCheck size={13}/> Added
                                </>
                              ) : (
                                <>
                                  <FiShoppingCart size={13}/> Add to Cart
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}

            {isLoading && (
              <div className="chat-bubble assistant-bubble typing-bubble">
                <ThreeDots color="#0b69ff" height={16} width={34}/>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              placeholder="Ask about products or your cart..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={isLoading}
            />
            <button
              type="submit"
              className="chat-send-btn"
              disabled={isLoading || !inputMessage.trim()}
              aria-label="Send message"
            >
              <FiSend size={20}/>
            </button>
          </form>
        </div>
      )}
    </div>
  )
}

export default withRouter(ChatAssistant)